import Accordion from '@Common/Accordion';
import JiraAttachmentList from '@Common/JiraAttachmentList';
import TextModalInput from '@Common/TextModalInput';
import { useGeneratorContext } from '@Context/GeneratorContext';
import { useTestCaseContext } from '@Context/TestCaseContext';
import { IssueDescrip } from '@Models/types';
import { PencilSimple, ChatCircle } from '@phosphor-icons/react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import { JIRA_SANITIZE_OPTIONS } from '@Lib/sanitize';

const RequirementItems = () => {
  const {
    issueDescrips,
    selectedSubtasks,
    setSelectedSubtasks,
    updateSubtaskDescription,
  } = useGeneratorContext();

  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openContext, setOpenContext] = useState<boolean>(false);
  const [editReq, setEditReq] = useState<IssueDescrip>({
    description_body: '',
    summary: '',
    id: '',
    key: '',
  });

  const { generationStatus } = useTestCaseContext();

  const handleOpenEditModal = () => setOpenEdit(!openEdit);
  const handleOpenContextModal = () => setOpenContext(!openContext);

  const handleSaveContext = (context: string) => {
    if (editReq) {
      updateSubtaskDescription(editReq.id, editReq.description_body, context);
      handleOpenContextModal();
    }
  };

  const extractTextFromHTML = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const textContent = doc.body.textContent || '';
    const formattedText = textContent
      .replace(/<br\s*[/]?>/gi, ' ')
      .replace(/([.!?])\s*/g, '$1\n');
    return formattedText;
  };

  const handleRequirementSelection = (
    item: IssueDescrip,
    selected: boolean
  ) => {
    if (generationStatus !== 'In Progress') {
      const reqWithContext =
        issueDescrips.find((r) => r.id === item.id) || item;

      const updatedSelectedItems = selected
        ? [...selectedSubtasks, reqWithContext]
        : selectedSubtasks.filter((i) => i.key !== item.key);
      setSelectedSubtasks(updatedSelectedItems);
    } else {
      toast.error('Ongoing Process!');
    }
  };

  const renderRequirements = (item: IssueDescrip) => {
    const handleEditButton = () => {
      setEditReq({
        summary: item.summary,
        description_body: extractTextFromHTML(item.description_body),
        id: item.id,
        key: item.key,
      });
      handleOpenEditModal();
    };

    const handleContextButton = () => {
      const currentSubtask = issueDescrips.find((r) => r.id === item.id) || {
        summary: item.summary,
        description_body: item.description_body,
        id: item.id,
        key: item.key,
      };
      setEditReq(currentSubtask);
      handleOpenContextModal();
    };

    return (
      <div className="flex flex-col">
        <div
          className="adf-body"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              item.description_body || '',
              JIRA_SANITIZE_OPTIONS
            ),
          }}
        />
        <JiraAttachmentList attachments={item.attachment} />
        <div className="flex justify-end">
          <button
            data-testid={`test-context-btn-${item.id}`}
            className="relative mb-2 mr-2 mt-5 flex items-center justify-center space-x-2 whitespace-nowrap rounded-md bg-primary-700 px-2 py-2 pr-5 text-xs text-white"
            onClick={handleContextButton}
            title="Add Context"
          >
            <ChatCircle size={12} />
            <span>Add Context</span>
            {issueDescrips.find((r) => r.id === item.id)?.context && (
              <div className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-green-400" />
            )}
          </button>
          <button
            data-testid={`test-edit-btn-${item.id}`}
            className="relative mb-2 mt-5 flex items-center justify-center space-x-2 whitespace-nowrap rounded-md bg-primary-700 px-2 py-2 pr-5 text-xs text-white"
            onClick={handleEditButton}
          >
            <PencilSimple size={12} />
            <span>Edit</span>
          </button>
        </div>
      </div>
    );
  };

  const handleSubmit = (input: string) => {
    updateSubtaskDescription(editReq.id, input);
    setSelectedSubtasks([]);
    setOpenEdit(false);
  };

  return (
    <>
      <div
        id="req-items"
        data-testid="test-req-items"
        className="custom-scroll flex h-0 flex-grow flex-col overflow-y-auto text-xs sm:text-sm"
      >
        <Accordion
          selectedItems={selectedSubtasks}
          items={issueDescrips}
          onProcess={handleRequirementSelection}
          renderContent={renderRequirements}
        />
      </div>
      <TextModalInput
        isOpen={openEdit}
        onClose={handleOpenEditModal}
        onSubmit={handleSubmit}
        value={editReq.description_body}
        title={editReq.summary}
      />
      <TextModalInput
        isOpen={openContext}
        onClose={handleOpenContextModal}
        onSubmit={handleSaveContext}
        title={`Context for ${editReq?.summary || ''}`}
        information={{
          title: 'Add Context',
          description:
            'This context will be used alongside the requirement description to generate test cases.',
        }}
      />
    </>
  );
};

export default RequirementItems;
